import { FormEvent, useEffect, useState } from 'react';
import { getSiteSettings, submitPartnerInquiry } from '../services/api.shop';
import { useTranslation } from '../context/TranslationContext';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';
import { ValidationError } from '../admin/components/ValidationError';
import { parseAxiosValidationErrors } from '../utils/productValidation';

function tf(t: (key: string) => string, key: string, fallback: string): string {
    const value = t(key);
    return value === key ? fallback : value;
}

type FieldErrors = Record<string, string>;

const Partner = () => {
    const { t } = useTranslation();
    const [contactEmail, setContactEmail] = useState('support@petito.be');
    const [companyName, setCompanyName] = useState('');
    const [contactName, setContactName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [inquiryType, setInquiryType] = useState<'brand-partnership' | 'investor'>('brand-partnership');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    useEffect(() => {
        getSiteSettings()
            .then((settings) => {
                if (settings.contactEmail) setContactEmail(settings.contactEmail);
            })
            .catch(() => { /* use fallback */ });
    }, []);

    const clearFieldError = (field: string) => {
        setFieldErrors((prev) => {
            if (!prev[field] && !prev.General) return prev;
            const next = { ...prev };
            delete next[field];
            delete next.General;
            return next;
        });
    };

    const validateClient = (): FieldErrors => {
        const errors: FieldErrors = {};
        if (!companyName.trim()) {
            errors.companyName = tf(t, 'partner.form.errors.companyRequired', 'Company name is required.');
        } else if (companyName.trim().length > 200) {
            errors.companyName = tf(t, 'partner.form.errors.companyLength', 'Company name must be at most 200 characters.');
        }

        if (!contactName.trim()) {
            errors.contactName = tf(t, 'partner.form.errors.contactRequired', 'Contact name is required.');
        } else if (contactName.trim().length > 200) {
            errors.contactName = tf(t, 'partner.form.errors.contactLength', 'Contact name must be at most 200 characters.');
        }

        if (!email.trim()) {
            errors.email = tf(t, 'partner.form.errors.emailRequired', 'Email is required.');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            errors.email = tf(t, 'partner.form.errors.emailInvalid', 'Enter a valid email address.');
        } else if (email.trim().length > 320) {
            errors.email = tf(t, 'partner.form.errors.emailLength', 'Email must be at most 320 characters.');
        }

        if (phone.trim().length > 50) {
            errors.phone = tf(t, 'partner.form.errors.phoneLength', 'Phone must be at most 50 characters.');
        }

        if (!message.trim()) {
            errors.message = tf(t, 'partner.form.errors.messageRequired', 'Message is required.');
        } else if (message.trim().length > 2000) {
            errors.message = tf(t, 'partner.form.errors.messageLength', 'Message must be at most 2000 characters.');
        }

        return errors;
    };

    /** Normalize backend keys (CompanyName / companyName) to camelCase field names. */
    const normalizeServerErrors = (raw: Record<string, string>): FieldErrors => {
        const map: Record<string, string> = {
            companyname: 'companyName',
            contactname: 'contactName',
            email: 'email',
            phone: 'phone',
            message: 'message',
            inquirytype: 'inquiryType',
            general: 'General',
        };
        const out: FieldErrors = {};
        for (const [key, value] of Object.entries(raw)) {
            const normalized = map[key.replace(/\./g, '').toLowerCase()] ?? key;
            out[normalized] = value;
        }
        return out;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setSuccess(false);

        const clientErrors = validateClient();
        if (Object.keys(clientErrors).length > 0) {
            setFieldErrors(clientErrors);
            scrollToFirstErrorField(clientErrors);
            setSubmitting(false);
            return;
        }

        setFieldErrors({});

        try {
            await submitPartnerInquiry({
                companyName: companyName.trim(),
                contactName: contactName.trim(),
                email: email.trim(),
                phone: phone.trim() || undefined,
                message: message.trim(),
                inquiryType,
            });
            setSuccess(true);
            setCompanyName('');
            setContactName('');
            setEmail('');
            setPhone('');
            setMessage('');
        } catch (err: unknown) {
            const parsed = normalizeServerErrors(parseAxiosValidationErrors(err) as Record<string, string>);
            if (Object.keys(parsed).length === 0) {
                parsed.General = tf(
                    t,
                    'partner.form.error',
                    'Something went wrong. Please try again or email us directly.'
                );
            }
            setFieldErrors(parsed);
            scrollToFirstErrorField(parsed);
        } finally {
            setSubmitting(false);
        }
    };

    const mailtoSubject = encodeURIComponent(
        inquiryType === 'investor' ? 'Investor partnership inquiry' : 'Brand partnership inquiry'
    );
    const mailtoHref = `mailto:${contactEmail}?subject=${mailtoSubject}`;

    const inputClass = (field: string) =>
        `w-full rounded-lg border bg-[var(--bg-color)] px-4 py-2 text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-color)] ${
            fieldErrors[field]
                ? 'border-red-300 bg-red-50/30'
                : 'border-[var(--accent-color)]'
        }`;

    return (
        <div className="mx-auto max-w-5xl">
            <section className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-[var(--accent-color)] to-[color-mix(in_srgb,var(--text-color),transparent_60%)] px-5 py-10 text-center text-[var(--text-color)] shadow-lg sm:mb-12 sm:rounded-3xl sm:px-8 sm:py-16 md:px-16">
                <div className="relative z-10">
                    <p className="mb-3 text-sm font-semibold uppercase tracking-widest opacity-90">
                        {tf(t, 'partner.hero.eyebrow', 'Partnerships')}
                    </p>
                    <h1 className="mb-4 text-2xl font-bold sm:text-4xl md:text-5xl">
                        {tf(t, 'partner.hero.title', 'Grow with Petito')}
                    </h1>
                    <p className="mx-auto max-w-2xl text-base opacity-95 sm:text-lg">
                        {tf(
                            t,
                            'partner.hero.subtitle',
                            "Join Europe's circular marketplace for families. Partner with us as a brand or investor and help build a more sustainable future for parents and children."
                        )}
                    </p>
                </div>
                <div className="pointer-events-none absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/10" />
                <div className="pointer-events-none absolute -bottom-16 -left-10 h-64 w-64 rounded-full bg-white/10" />
            </section>

            <div className="mb-12 grid gap-8 md:grid-cols-2">
                <article className="rounded-2xl border border-[var(--accent-color)] bg-[color-mix(in_srgb,var(--bg-color),transparent_5%)] p-5 sm:p-8">
                    <h2 className="mb-3 text-xl font-bold text-[var(--text-color)] sm:text-2xl">
                        {tf(t, 'partner.brand.title', 'Brand partnerships')}
                    </h2>
                    <p className="text-[var(--text-color)] opacity-90">
                        {tf(
                            t,
                            'partner.brand.text',
                            'Collaborate with Petito to reach conscious parents across Belgium and beyond. Co-create campaigns, featured collections, and circular initiatives that put quality baby products back into loving homes.'
                        )}
                    </p>
                </article>
                <article className="rounded-2xl border border-[var(--accent-color)] bg-[color-mix(in_srgb,var(--bg-color),transparent_5%)] p-5 sm:p-8">
                    <h2 className="mb-3 text-xl font-bold text-[var(--text-color)] sm:text-2xl">
                        {tf(t, 'partner.investor.title', 'Investor relations')}
                    </h2>
                    <p className="text-[var(--text-color)] opacity-90">
                        {tf(
                            t,
                            'partner.investor.text',
                            'We are building a trusted resale platform with strong community roots and scalable technology. Connect with our team to explore growth opportunities in the circular economy.'
                        )}
                    </p>
                </article>
            </div>

            <section className="rounded-2xl border border-[var(--accent-color)] bg-[color-mix(in_srgb,var(--bg-color),transparent_5%)] p-8 shadow-sm">
                <h2 className="mb-2 text-2xl font-bold text-[var(--text-color)]">
                    {tf(t, 'partner.form.title', 'Get in touch')}
                </h2>
                <p className="mb-6 text-sm text-[var(--text-color)] opacity-80">
                    {tf(t, 'partner.form.subtitle', 'Tell us about your organisation. We typically respond within 2 business days.')}{' '}
                    <a href={mailtoHref} className="font-medium text-[var(--accent-color)] underline">
                        {contactEmail}
                    </a>
                </p>

                {success && (
                    <div className="mb-6 rounded-lg bg-[color-mix(in_srgb,var(--accent-color),transparent_60%)] px-4 py-3 text-sm font-medium text-[var(--text-color)]">
                        {tf(t, 'partner.form.success', 'Thank you! Your inquiry has been received.')}
                    </div>
                )}

                {fieldErrors.General && (
                    <div className="mb-4">
                        <ValidationError error={fieldErrors.General} />
                        <a href={mailtoHref} className="ml-2 font-medium underline text-sm text-red-700">
                            {tf(t, 'partner.form.emailfallback', 'Email us instead')}
                        </a>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="grid gap-5 md:grid-cols-2" noValidate>
                    <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]" htmlFor="inquiryType">
                            {tf(t, 'partner.form.type', 'Inquiry type')}
                        </label>
                        <select
                            id="inquiryType"
                            name="inquiryType"
                            value={inquiryType}
                            onChange={(e) => {
                                setInquiryType(e.target.value as 'brand-partnership' | 'investor');
                                clearFieldError('inquiryType');
                            }}
                            className={inputClass('inquiryType')}
                        >
                            <option value="brand-partnership">{tf(t, 'partner.form.type.brand', 'Brand partnership')}</option>
                            <option value="investor">{tf(t, 'partner.form.type.investor', 'Investor')}</option>
                        </select>
                        <ValidationError error={fieldErrors.inquiryType} />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]" htmlFor="companyName">
                            {tf(t, 'partner.form.company', 'Company name')} *
                        </label>
                        <input
                            id="companyName"
                            name="companyName"
                            value={companyName}
                            onChange={(e) => {
                                setCompanyName(e.target.value);
                                clearFieldError('companyName');
                            }}
                            className={inputClass('companyName')}
                            autoComplete="organization"
                        />
                        <ValidationError error={fieldErrors.companyName} />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]" htmlFor="contactName">
                            {tf(t, 'partner.form.contact', 'Contact name')} *
                        </label>
                        <input
                            id="contactName"
                            name="contactName"
                            value={contactName}
                            onChange={(e) => {
                                setContactName(e.target.value);
                                clearFieldError('contactName');
                            }}
                            className={inputClass('contactName')}
                            autoComplete="name"
                        />
                        <ValidationError error={fieldErrors.contactName} />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]" htmlFor="email">
                            {tf(t, 'partner.form.email', 'Email')} *
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                clearFieldError('email');
                            }}
                            className={inputClass('email')}
                            autoComplete="email"
                        />
                        <ValidationError error={fieldErrors.email} />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]" htmlFor="phone">
                            {tf(t, 'partner.form.phone', 'Phone')}
                        </label>
                        <input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => {
                                setPhone(e.target.value);
                                clearFieldError('phone');
                            }}
                            className={inputClass('phone')}
                            autoComplete="tel"
                        />
                        <ValidationError error={fieldErrors.phone} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-[var(--text-color)]" htmlFor="message">
                            {tf(t, 'partner.form.message', 'Message')} *
                        </label>
                        <textarea
                            id="message"
                            name="message"
                            rows={5}
                            value={message}
                            onChange={(e) => {
                                setMessage(e.target.value);
                                clearFieldError('message');
                            }}
                            className={inputClass('message')}
                        />
                        <ValidationError error={fieldErrors.message} />
                    </div>
                    <div className="md:col-span-2">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="rounded-lg bg-[var(--accent-color)] px-8 py-3 font-semibold text-white hover:opacity-90 disabled:opacity-50"
                        >
                            {submitting
                                ? tf(t, 'partner.form.sending', 'Sending…')
                                : tf(t, 'partner.form.submit', 'Send inquiry')}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
};

export default Partner;
